import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { api } from '../services/api-client.js';
import { formatMoney } from '@store/shared';
import { LoadingSpinner } from '../components/LoadingSpinner.jsx';
import { ErrorMessage } from '../components/ErrorMessage.jsx';

export function OrderTrackingPage() {
  const { language, t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialOrderParam = searchParams.get('order') || '';
  const initialTokenParam = searchParams.get('token') || '';

  const [orderNumber, setOrderNumber] = useState(initialOrderParam);
  const [trackingToken, setTrackingToken] = useState(initialTokenParam);
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Auto-fill from sessionStorage if empty
  useEffect(() => {
    if (!orderNumber || !trackingToken) {
      try {
        const savedOrder = sessionStorage.getItem('last_order_number');
        const savedToken = sessionStorage.getItem('last_tracking_token');
        if (savedOrder && !orderNumber) setOrderNumber(savedOrder);
        if (savedToken && !trackingToken) setTrackingToken(savedToken);
      } catch {}
    }
  }, []);

  // Fetch status if both params provided in URL on mount
  useEffect(() => {
    if (initialOrderParam && initialTokenParam) {
      performTracking(initialOrderParam, initialTokenParam);
    }
  }, [initialOrderParam, initialTokenParam]);

  const performTracking = async (numberToTrack, tokenToUse) => {
    if (!numberToTrack.trim() || !tokenToUse.trim()) {
      setErrorMsg('Veuillez renseigner votre numéro de commande et votre clé de suivi.');
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setOrderData(null);

    try {
      const response = await api.getOrderStatus(numberToTrack.trim(), tokenToUse.trim());
      if (response?.data) {
        setOrderData(response.data);
      }
    } catch (err) {
      setErrorMsg(
        err.status === 403 || err.status === 401
          ? 'Clé de suivi invalide ou non autorisée pour cette commande.'
          : err.status === 404
          ? `La commande '${numberToTrack}' est introuvable.`
          : err.message || 'Impossible de récupérer les informations de suivi.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSearchParams({ order: orderNumber.trim(), token: trackingToken.trim() });
    performTracking(orderNumber, trackingToken);
  };

  return (
    <div className="container order-tracking-page" style={{ maxWidth: '720px', padding: '2rem 1rem' }}>
      <div style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '2rem', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem', color: 'var(--color-primary)' }}>
          📦 {t('trackTitle')}
        </h1>
        <p style={{ marginBottom: '1.75rem', color: 'var(--color-text-muted)' }}>
          {t('trackSubtitle')}
        </p>

        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.375rem', fontSize: '0.875rem' }}>
              {t('trackingNumberInput')} *
            </label>
            <input
              type="text"
              required
              placeholder="ex: CMD-20260816-K7P4M"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="font-mono ltr-only"
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-bg)',
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.375rem', fontSize: '0.875rem' }}>
              {t('trackingTokenInput')} *
            </label>
            <input
              type="password"
              required
              placeholder="Clé de sécurité à 48 caractères"
              value={trackingToken}
              onChange={(e) => setTrackingToken(e.target.value)}
              className="font-mono ltr-only"
              style={{
                width: '100%',
                padding: '0.625rem 0.875rem',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-bg)',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{ marginTop: '0.5rem', width: '100%', padding: '0.75rem' }}
          >
            {loading ? t('loading') : `🔍 ${t('trackBtn')}`}
          </button>
        </form>
      </div>

      {loading && <LoadingSpinner />}
      {errorMsg && <ErrorMessage message={errorMsg} />}

      {/* Tracking Result View */}
      {orderData && (
        <div style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '2rem' }}>
          {/* Status Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', paddingBottom: '1.25rem', borderBottom: '1px solid var(--color-border)', marginBottom: '1.5rem' }}>
            <div>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>Commande</span>
              <h2 className="font-mono" style={{ fontSize: '1.25rem', color: 'var(--color-primary)' }}>
                {orderData.orderNumber}
              </h2>
            </div>

            <div style={{ textAlign: language === 'ar' ? 'left' : 'right' }}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>{t('trackingStatusLabel')}</span>
              <div>
                <span className="product-badge badge-new" style={{ position: 'static', display: 'inline-block', fontSize: '0.875rem', padding: '0.375rem 0.75rem' }}>
                  {orderData.statusLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Items Summary */}
          {orderData.items && orderData.items.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.75rem' }}>{t('orderItemsTitle')}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {orderData.items.map((it, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', padding: '0.5rem', backgroundColor: 'var(--color-surface-subtle)', borderRadius: 'var(--radius-sm)' }}>
                    <span>{it.quantity}x {it.name}</span>
                    <strong>{formatMoney(it.totalCents, orderData.currency || 'MAD', language)}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Financial Totals */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: '1rem', borderTop: '1px solid var(--color-border)', marginBottom: '1.5rem' }}>
            <span style={{ fontWeight: 600 }}>{t('totalToPay')} (COD) :</span>
            <strong style={{ fontSize: '1.25rem', color: 'var(--color-primary)' }}>
              {formatMoney(orderData.totalCents, orderData.currency || 'MAD', language)}
            </strong>
          </div>

          {/* Public Timeline */}
          {orderData.timeline && orderData.timeline.length > 0 && (
            <div>
              <h3 style={{ fontSize: '1rem', marginBottom: '1rem' }}>{t('timelineTitle')}</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', borderInlineStart: '2px solid var(--color-primary-border)', paddingInlineStart: '1.25rem' }}>
                {orderData.timeline.map((event, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <div
                      style={{
                        position: 'absolute',
                        insetInlineStart: '-1.625rem',
                        top: '0.25rem',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary)',
                      }}
                    />
                    <strong style={{ fontSize: '0.875rem' }}>{event.statusLabel}</strong>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-subtle)' }}>
                      {new Date(event.timestamp).toLocaleString(language === 'ar' ? 'ar-MA' : 'fr-FR')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
